import { invariant } from '@backmarket/utils/function/invariant'
import { resolvePath } from '@backmarket/utils/url/resolvePath'

import type { ApiSpecs } from '../specs'

type PathSegments<Path extends string> =
  Path extends `/${infer A extends string}/${infer Rest extends string}`
    ? A | PathSegments<Rest>
    : Path extends `${infer A extends string}/${infer Rest extends string}`
      ? A | PathSegments<Rest>
      : Path

type OnlyDynamicParams<Segment extends string> =
  Segment extends `:${infer Param}` ? Param : never

type DynamicParams<Path extends string> = OnlyDynamicParams<PathSegments<Path>>

type PathParams<Path extends string> =
  DynamicParams<Path> extends never
    ? {
        pathParams?: never
      }
    : { pathParams: Record<DynamicParams<Path>, string | number> }

export interface HttpStreamOptions<Path extends string = string> {
  /**
   * Query parameters to include in the request
   */
  query?: Record<string, string | number | boolean | undefined>

  /**
   * Method to use for the request
   */
  method?: 'GET' | 'POST'

  /**
   * Body to include in the request
   */
  body?: Record<string, unknown>

  /**
   * Additional headers to include in the request
   */
  headers?: Record<string, string>

  /**
   * Base URL to use for the request (overrides global base URL)
   */
  baseURL?: string

  /**
   * Path parameters for dynamic routes
   */
  pathParams?: PathParams<Path>['pathParams']

  /**
   * Callback invoked when a new event is received
   */
  onEvent?: (event: unknown) => void

  /**
   * Callback invoked when the stream encounters an error
   */
  onError?: (error: Error) => void

  /**
   * Callback invoked when the stream ends.
   * @param terminatedBySentinel true if ended by the `data: done` sentinel,
   *   false if the connection closed naturally without one.
   */
  onEnd?: (terminatedBySentinel: boolean) => void

  /**
   * AbortSignal to cancel the stream
   */
  signal?: AbortSignal

  /**
   * Callback invoked when the stream is intentionally aborted via signal
   */
  onAbort?: () => void
}

export interface HttpStream {
  /**
   * Start streaming events from a Server-Sent Events (SSE) endpoint
   */
  <Path extends keyof ApiSpecs & string>(
    path: Path,
    options?: HttpStreamOptions<Path>,
  ): Promise<ReadableStreamDefaultReader<Uint8Array>>

  /**
   * Create a new HttpStream instance with custom base URL or headers
   */
  create: (options: {
    baseURL?: string
    headers?: Record<string, string>
  }) => HttpStream
}

/**
 * Creates an HTTP streaming client for Server-Sent Events (SSE)
 */
export function createHttpStream(globalOptions?: {
  getDefaultBaseUrl?: () => string
  getDefaultHeaders?: () => Record<string, string>
}): HttpStream {
  async function stream<Path extends keyof ApiSpecs & string>(
    path: Path,
    options: HttpStreamOptions<Path> = {},
  ): Promise<ReadableStreamDefaultReader<Uint8Array>> {
    const resolvedPath = resolvePath(path, options.pathParams)

    // Build query string if query params are provided
    let url = resolvedPath
    if (options.query) {
      const params = new URLSearchParams()

      Object.entries(options.query).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value))
        }
      })

      const queryString = params.toString()
      if (queryString) {
        url = `${resolvedPath}?${queryString}`
      }
    }

    // Prepend base URL if provided (use option baseURL, then global getter)
    const baseURL = options.baseURL ?? globalOptions?.getDefaultBaseUrl?.()
    if (baseURL) {
      url = `${baseURL}${url}`
    }

    // Merge headers (use global getter, then option headers)
    const defaultHeaders = globalOptions?.getDefaultHeaders?.() ?? {}
    const headers = {
      Accept: 'text/event-stream',
      ...defaultHeaders,
      ...options.headers,
      'Accept-Encoding': 'identity',
    }

    const response = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    })

    if (!response.ok) {
      throw new Error(
        `HTTP Stream Error: ${response.status} ${response.statusText}`,
      )
    }

    const reader = response.body?.getReader()

    if (!reader) {
      throw new Error('Response body reader is not available')
    }

    // Start processing the stream in the background
    if (
      options.onEvent ||
      options.onError ||
      options.onEnd ||
      options.onAbort
    ) {
      processStream(reader, options).catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          options.onAbort?.()

          return
        }
        if (options.onError) {
          options.onError(err)
        } else {
          // eslint-disable-next-line no-console
          console.error('Unhandled stream error:', err)
        }
      })
    }

    return reader
  }

  stream.create = (overrides: {
    baseURL?: string
    headers?: Record<string, string>
  }) => {
    return createHttpStream({
      getDefaultBaseUrl: overrides.baseURL
        ? () => overrides.baseURL ?? ''
        : globalOptions?.getDefaultBaseUrl,
      getDefaultHeaders: () => ({
        ...globalOptions?.getDefaultHeaders?.(),
        ...overrides.headers,
      }),
    })
  }

  return stream as HttpStream
}

/**
 * Process the SSE stream and invoke callbacks
 */
async function processStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  options: HttpStreamOptions,
): Promise<void> {
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read()

      if (done) {
        if (options.onEnd) {
          options.onEnd(false)
        }
        break
      }

      // Decode and append to buffer
      buffer += decoder.decode(value, { stream: true })

      // Process complete lines
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6)

          // Check for end signal
          if (dataStr === 'done') {
            if (options.onEnd) {
              options.onEnd(true)
            }

            return
          }

          try {
            const eventData = JSON.parse(dataStr)

            if (options.onEvent) {
              options.onEvent(eventData)
            }
          } catch (parseError) {
            // eslint-disable-next-line no-console
            console.warn('Failed to parse SSE event:', dataStr, parseError)
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Retrieve the HTTP stream instance for SSE endpoints.
 * This composable reuses the same base URL and headers configuration as useHttpFetch.
 */
export function useHttpStream(): HttpStream {
  // SSE is primarily a client-side feature
  if (import.meta.server) {
    throw new Error('useHttpStream is not supported on the server side')
  }

  // Access the global $httpStream instance which is injected by the plugin
  invariant(window.$httpStream, 'window.$httpStream is missing')

  return window.$httpStream
}
